
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  ArrowLeft, 
  Home, 
  RefreshCw, 
  Menu,
  Share2,
  Search,
  Settings,
  ChevronLeft,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const MobileBrowser: React.FC = () => {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<string>('home');

  // Handle navigation state changes
  useEffect(() => {
    const handleRouteChange = () => {
      // Update page title based on URL
      const path = window.location.pathname;
      if (path === '/') setCurrentPage('home');
      else if (path.includes('collaborate')) setCurrentPage('collaborate');
      else setCurrentPage('other');
    };
    
    handleRouteChange();
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRefresh = () => {
    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    window.location.reload();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate([30, 30, 30]);
      }
      
      toast.info(`${language === 'ur' ? 'تلاش کیا گیا: ' : 'Searched for: '} ${searchQuery}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleShare = async () => {
    // Add haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      setShareDialogOpen(true);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        toast.success(language === 'ur' ? 'لنک کاپی ہو گیا' : 'Link copied to clipboard');
        setShareDialogOpen(false);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
      });
  };

  // Only render on mobile devices
  if (!isMobile) return null;

  return (
    <>
      {/* Mobile app header - modern design */}
      <div className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-900 shadow-sm z-50 border-b ios-header-fix">
        <div className="flex items-center justify-between px-4 py-2.5">
          {/* Left Actions */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={handleGoBack} className="h-9 w-9 rounded-full">
              <ArrowLeft size={18} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleGoHome} className="h-9 w-9 rounded-full">
              <Home size={18} />
            </Button>
          </div>
          
          {/* App Title */}
          <div className="flex-1 text-center flex flex-col items-center justify-center">
            <div className="font-semibold text-base transition-opacity">
              {language === 'ur' ? 'عدالت' : 'Adalat'}
            </div>
          </div>
          
          {/* Right Actions */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)} className="h-9 w-9 rounded-full">
              <Search size={18} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(true)} className="h-9 w-9 rounded-full">
              <Menu size={18} />
            </Button>
          </div>
        </div>
      </div>

      {/* Modern Menu Sheet */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side={language === 'ur' ? "left" : "right"} className="w-[85vw] p-0 border-l border-gray-200 dark:border-gray-800">
          <SheetHeader className="border-b p-4 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between w-full">
              <SheetTitle className={`${language === 'ur' ? 'font-urdu' : ''} text-lg`}>
                {language === 'ur' ? 'مینو' : 'Menu'}
              </SheetTitle>
              <SheetClose asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                  <X size={16} />
                </Button>
              </SheetClose>
            </div>
          </SheetHeader>
          <div className="flex flex-col p-1 gap-1 overflow-y-auto max-h-[80vh]">
            <Button 
              variant="ghost" 
              className={`justify-${language === 'ur' ? 'end' : 'start'} h-12 rounded-lg my-0.5`} 
              onClick={handleGoHome}
            >
              <Home size={18} className={`${language === 'ur' ? 'ml-3' : 'mr-3'}`} />
              <span className="text-base">{language === 'ur' ? 'ہوم پیج' : 'Home'}</span>
            </Button>
            <Button 
              variant="ghost" 
              className={`justify-${language === 'ur' ? 'end' : 'start'} h-12 rounded-lg my-0.5`} 
              onClick={handleRefresh}
            >
              <RefreshCw size={18} className={`${language === 'ur' ? 'ml-3' : 'mr-3'}`} />
              <span className="text-base">{language === 'ur' ? 'ریفریش' : 'Refresh'}</span>
            </Button>
            <Button 
              variant="ghost" 
              className={`justify-${language === 'ur' ? 'end' : 'start'} h-12 rounded-lg my-0.5`} 
              onClick={handleShare}
            >
              <Share2 size={18} className={`${language === 'ur' ? 'ml-3' : 'mr-3'}`} />
              <span className="text-base">{language === 'ur' ? 'شیئر کریں' : 'Share'}</span>
            </Button>
            <Button 
              variant="ghost" 
              className={`justify-${language === 'ur' ? 'end' : 'start'} h-12 rounded-lg my-0.5`}
            >
              <Settings size={18} className={`${language === 'ur' ? 'ml-3' : 'mr-3'}`} />
              <span className="text-base">{language === 'ur' ? 'ترتیبات' : 'Settings'}</span>
            </Button>
            <SheetClose asChild>
              <Button variant="default" className="mt-4 h-12 bg-primary hover:bg-primary/90 mx-4">
                {language === 'ur' ? 'بند کریں' : 'Close'}
              </Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>

      {/* Improved Search Dialog */}
      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0">
          <DialogHeader className="p-4 border-b">
            <div className="flex items-center justify-between w-full">
              <DialogTitle className="text-lg">
                {language === 'ur' ? 'تلاش کریں' : 'Search'}
              </DialogTitle>
              <DialogClose asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                  <X size={16} />
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>
          <form onSubmit={handleSearch} className="flex gap-2 p-4">
            <Input 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              placeholder={language === 'ur' ? 'تلاش کریں' : 'Search query'} 
              className="flex-1 h-11 text-base"
              autoFocus
            />
            <Button type="submit" className="h-11">
              {language === 'ur' ? 'تلاش' : 'Search'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md p-0">
          <DialogHeader className="p-4 border-b">
            <div className="flex items-center justify-between w-full">
              <DialogTitle className="text-lg">
                {language === 'ur' ? 'شیئر کریں' : 'Share'}
              </DialogTitle>
              <DialogClose asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                  <X size={16} />
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>
          <div className="flex flex-col gap-4 p-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border text-center overflow-hidden">
              <p className="text-sm mb-2 text-muted-foreground">
                {language === 'ur' ? 'موجودہ صفحہ کا لنک' : 'Current page URL'}
              </p>
              <p className="text-xs bg-white dark:bg-gray-900 p-2 rounded border truncate font-mono">
                {window.location.href}
              </p>
            </div>
            <Button onClick={copyToClipboard} className="h-11">
              {language === 'ur' ? 'لنک کاپی کریں' : 'Copy Link'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bottom spacer for mobile navigation */}
      <div className="pb-16 safe-area-bottom"></div>
    </>
  );
};

export default MobileBrowser;
